// src/components/Search/SeasonEpisodeSelector.js

import React, { useState, useEffect, forwardRef } from 'react';
import { ClipLoader } from 'react-spinners';

const SeasonEpisodeSelector = forwardRef(({ onSubmit, seasonsData, server, loadingSeasons }, ref) => {
  const [season, setSeason] = useState('');
  const [episode, setEpisode] = useState('');
  const [error, setError] = useState('');
  const [episodesOptions, setEpisodesOptions] = useState([]);

  // Handle changes when season is selected
  useEffect(() => {
    if (season && seasonsData && seasonsData.length > 0) {
      // Determine episodes based on server
      if (server === 'Imdb') {
        // Find the selected season's episodes
        const selectedSeason = seasonsData.find(
          (s) => s.seasonNumber === parseInt(season, 10)
        );
        if (selectedSeason && selectedSeason.episodes) {
          setEpisodesOptions(selectedSeason.episodes);
          setEpisode(''); // Reset episode selection
        } else {
          setEpisodesOptions([]);
        }
      } else if (server === 'Tmdb') {
        // For Tmdb: find the selected season's episode count
        const selectedSeason = seasonsData.find(
          (s) => s.seasonNumber === parseInt(season, 10)
        );
        if (selectedSeason && selectedSeason.episodeCount) {
          const maxEpisode = selectedSeason.episodeCount;
          const episodes = Array.from({ length: maxEpisode }, (_, i) => i + 1);
          setEpisodesOptions(episodes);
          setEpisode(''); // Reset episode selection
        } else {
          setEpisodesOptions([]);
        }
      } else {
        // If any other server is implemented in the future, handle here
        setEpisodesOptions([]);
        setEpisode('');
      }
    } else {
      setEpisodesOptions([]);
      setEpisode('');
    }
  }, [season, seasonsData, server]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (season && episode) {
      onSubmit(parseInt(season, 10), parseInt(episode, 10));
      setError('');
    } else {
      setError('Please select both season and episode.');
    }
  };

  // If data is still loading (or empty), show a loading spinner/message
  if (loadingSeasons || !seasonsData) {
    return (
      <div className="flex justify-center items-center mt-8">
        <div className="bg-glassOverlay backdrop-blur-md p-6 rounded-lg shadow-lg mx-auto max-w-md w-full flex flex-col items-center space-y-4">
          <ClipLoader color="#ffffff" size={40} />
          <p className="text-sm text-gray-300">
            Loading seasons and episodes...
          </p>
        </div>
      </div>
    );
  }

  // If we have no seasons data yet (although not loading), just show a waiting message
  if (seasonsData.length === 0) {
    return (
      <div className="flex justify-center items-center mt-8">
        <div className="bg-glassOverlay backdrop-blur-md p-6 rounded-lg shadow-lg mx-auto max-w-md w-full flex flex-col items-center space-y-4">
          <p className="text-sm text-gray-300">
            No season data available yet, please wait...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center mt-8">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col items-center justify-center bg-glassOverlay backdrop-blur-md p-6 rounded-lg shadow-lg mx-auto max-w-md w-full"
      >
        <h3 className="text-lg font-semibold text-white mb-4 text-center">
          Select Season &amp; Episode
        </h3>

        {/* Season Dropdown */}
        <div className="w-full mb-4">
          <label htmlFor="season" className="sr-only">
            Season
          </label>
          <select
            id="season"
            ref={ref}
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 text-white rounded-md focus:ring-4 focus:ring-accentBlue transition duration-300 appearance-none"
            required
          >
            <option value="">Select Season</option>
            {seasonsData.map((s) => (
              <option key={s.seasonNumber} value={s.seasonNumber}>
                Season {s.seasonNumber}
              </option>
            ))}
          </select>
        </div>

        {/* Episode Dropdown */}
        {season && (
          <div className="w-full mb-4">
            {server === 'Imdb' ? (
              <select
                id="episode"
                value={episode}
                onChange={(e) => setEpisode(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded-md focus:ring-4 focus:ring-accentBlue transition duration-300 appearance-none"
                required
              >
                <option value="">Select Episode</option>
                {episodesOptions.map((ep) => (
                  <option key={ep.episodeNumber} value={ep.episodeNumber}>
                    Episode {ep.episodeNumber}: {ep.title} ({ep.airDate}, Rating: {ep.rating})
                  </option>
                ))}
              </select>
            ) : server === 'Tmdb' ? (
              <select
                id="episode"
                value={episode}
                onChange={(e) => setEpisode(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded-md focus:ring-4 focus:ring-accentBlue transition duration-300 appearance-none"
                required
              >
                <option value="">Select Episode</option>
                {episodesOptions.map((ep) => (
                  <option key={ep} value={ep}>
                    Episode {ep}
                  </option>
                ))}
              </select>
            ) : (
              // If other servers that provide season/episode info are added, handle them here
              <select
                id="episode"
                value={episode}
                onChange={(e) => setEpisode(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 text-white rounded-md focus:ring-4 focus:ring-accentBlue transition duration-300 appearance-none"
                required
              >
                <option value="">Select Episode</option>
              </select>
            )}
          </div>
        )}

        {error && <p className="text-error text-sm mt-2">{error}</p>}

        <button
          type="submit"
          disabled={!(season && episode)}
          className={`mt-4 w-full ${
            season && episode
              ? 'bg-accentBlue hover:bg-accentBlueDark'
              : 'bg-gray-600 cursor-not-allowed'
          } text-white font-semibold py-2 rounded-lg shadow-lg transition transform hover:scale-105 duration-200`}
          aria-disabled={!(season && episode)}
        >
          Get Links
        </button>
      </form>
    </div>
  );
});

export default SeasonEpisodeSelector;
